import { isNativeCapacitorRuntime } from './revenueCatService';
import { dispatchMobileEvent, compactProperties } from './mobileAnalyticsClient';

// First-party mobile ACTIVATION instrumentation.
//
// "Activation" = a new mobile signup reaching a milestone that predicts
// retention/conversion. Today the app has no activation signal at all — nothing
// distinguishes a signed-up user who never took a quiz from an activated one,
// and the web onboarding funnel (absite_diagnostic_*) is GTM-only, so it is
// invisible on mobile (the build strips GTM/Mixpanel; only Segment survives).
//
// Ownership boundary: this workstream owns MOBILE activation; qlearn-pro owns
// WEB activation. So every activation event is gated to the native Capacitor
// runtime to avoid double-counting web signups against the web funnel.
//
// Each milestone fires at most once per user (localStorage-deduped, mirroring
// the `qlearn_paid_signup_tracked` dedupe used by web ad-conversion tracking),
// so re-mounts / re-logins don't inflate the activated count. Built on the
// shared mobileAnalyticsClient, so it inherits the never-throw guarantee.

export const MOBILE_ACTIVATION_MILESTONES = {
  FIRST_QUIZ_COMPLETED: 'first_quiz_completed',
  ONBOARDING_DIAGNOSTIC_COMPLETED: 'onboarding_diagnostic_completed',
  PROFILE_COMPLETED: 'profile_completed'
};

// Event name emitted to Segment/Mixpanel for every activation milestone; the
// specific milestone rides along as a property so the funnel is one event with
// a `milestone` dimension.
export const MOBILE_ACTIVATION_EVENT = 'Mobile Activation Milestone';

const DEDUPE_STORAGE_PREFIX = 'qlearn_mobile_activation_';

const isKnownMilestone = (milestone) =>
  Object.values(MOBILE_ACTIVATION_MILESTONES).includes(milestone);

// localStorage access is wrapped: private-mode / disabled storage must never
// throw into the app flow, and a storage failure should fail OPEN (fire the
// event) rather than silently drop activation signal.
const dedupeStorageKey = (milestone) => `${DEDUPE_STORAGE_PREFIX}${milestone}`;

export const hasTrackedMilestone = (milestone) => {
  try {
    return window.localStorage.getItem(dedupeStorageKey(milestone)) === 'true';
  } catch (storageError) {
    return false;
  }
};

const markMilestoneTracked = (milestone) => {
  try {
    window.localStorage.setItem(dedupeStorageKey(milestone), 'true');
  } catch (storageError) {
    // Storage unavailable (private mode) — we already fired the event; at worst
    // it fires again on a later session, which the warehouse can de-dupe.
  }
};

// Fire a mobile activation milestone. No-op off the native runtime, for an
// unknown milestone, or if already tracked for this user. Returns true if the
// event was dispatched. Never throws.
export const trackMobileActivationMilestone = (milestone, properties = {}) => {
  if (!isKnownMilestone(milestone)) {
    return false;
  }

  if (!isNativeCapacitorRuntime()) {
    return false;
  }

  if (hasTrackedMilestone(milestone)) {
    return false;
  }

  const payload = {
    platform: 'ios',
    category: 'activation',
    milestone,
    ...compactProperties(properties)
  };

  const dispatched = dispatchMobileEvent(MOBILE_ACTIVATION_EVENT, payload);

  // Mark tracked whenever we attempted a dispatch on the native runtime, so a
  // dropped Segment call doesn't cause the same milestone to re-fire forever.
  markMilestoneTracked(milestone);

  return dispatched;
};

export const trackMobileFirstQuizCompleted = (properties = {}) =>
  trackMobileActivationMilestone(MOBILE_ACTIVATION_MILESTONES.FIRST_QUIZ_COMPLETED, properties);

export const trackMobileOnboardingDiagnosticCompleted = (properties = {}) =>
  trackMobileActivationMilestone(MOBILE_ACTIVATION_MILESTONES.ONBOARDING_DIAGNOSTIC_COMPLETED, properties);

export const trackMobileProfileCompleted = (properties = {}) =>
  trackMobileActivationMilestone(MOBILE_ACTIVATION_MILESTONES.PROFILE_COMPLETED, properties);
